import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getGlobalImeiCode,
  getGlobalImeiCodeById,
  updateGlobalImei,
  updateGlobalImeiStatus,
  deleteGlobalImei,
} from "../../store/actions/GlobalImei.js";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Input,
  ButtonGroup,
} from "reactstrap";
import {
  EyeOutlined,
  UserOutlined,
  LoadingOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Breadcrumb } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import CustomToggleSwitch from "../../components/CustomToggleSwitch.js";

const GlobalImei = () => {
  document.title = "Global IMEI";
  const [isLoading, setIsLoading] = useState(true);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("Pending");
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImeiForDetails, setSelectedImeiForDetails] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const dispatch = useDispatch();
  const globalImeiCode = useSelector((state) => state.GlobalImei.list || []);

  useEffect(() => {
    const fetchImeiCodes = async () => {
      try {
        await dispatch(getGlobalImeiCode());
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching IMEI codes:", error);
        setIsLoading(false);
      }
    };

    fetchImeiCodes();
  }, [dispatch]);

  useEffect(() => {
    const fetchImeiCodes = async () => {
      try {
        const response = await dispatch(getGlobalImeiCode());
        console.log("API Response:", response); // Full response dekho
        console.log("First record images:", response?.[0]?.imagesLocal);
        console.log("First record imagesLive:", response?.[0]?.imagesLive);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching IMEI codes:", error);
        setIsLoading(false);
      }
    };

    fetchImeiCodes();
  }, [dispatch]);

  const formatImageSrc = (imageData) => {
    if (!imageData) return null;

    // Check if it's already a data URL
    if (imageData.startsWith("data:image/")) {
      return imageData;
    }

    // Check if it's a base64 string without prefix (starts with /9j/ for JPEG or iVBOR for PNG)
    if (imageData.startsWith("/9j/") || imageData.startsWith("iVBOR")) {
      // Determine image type
      let mimeType = "image/jpeg"; // default
      if (imageData.startsWith("iVBOR")) {
        mimeType = "image/png";
      } else if (imageData.startsWith("R0lGOD")) {
        mimeType = "image/gif";
      } else if (imageData.startsWith("UklGR")) {
        mimeType = "image/webp";
      }

      // Add data URL prefix
      return `data:${mimeType};base64,${imageData}`;
    }

    // If it's a URL path (starts with /uploads/)
    if (imageData.startsWith("/uploads/")) {
      const API_BASE_URL = import.meta.env.VITE_API_URL || "";
      return `${API_BASE_URL}${imageData}`;
    }

    // If it's already a full URL
    if (imageData.startsWith("http")) {
      return imageData;
    }

    return null;
  };

  // Update your handleViewImages function:
  const handleViewImages = (record) => {
    console.log("=== DEBUGGING IMAGES ===");
    console.log("Record ID:", record.id);

    // Collect all available images from various properties
    const allImages = [];
    const imageLabels = [];

    // Check and add individual image properties with labels
    const imageFields = [
      { key: "govIdProof", label: "Government ID Proof" },
      { key: "originalBoxPhoto", label: "Original Box Photo" },
      { key: "addressProofPhoto", label: "Address Proof Photo" },
      { key: "purchaseBillPhoto", label: "Purchase Bill Photo" },
      { key: "firPhoto", label: "FIR Photo" },
    ];

    imageFields.forEach(({ key, label }) => {
      if (record[key]) {
        const formattedImage = formatImageSrc(record[key]);
        if (formattedImage) {
          allImages.push(formattedImage);
          imageLabels.push(label);
        }
      }
    });

    // Add images from arrays if available
    if (record.imagesLive && record.imagesLive.length > 0) {
      record.imagesLive.forEach((img, index) => {
        const formattedImage = formatImageSrc(img);
        if (formattedImage) {
          allImages.push(formattedImage);
          imageLabels.push(`Live Image ${index + 1}`);
        }
      });
    }

    if (record.imagesLocal && record.imagesLocal.length > 0) {
      record.imagesLocal.forEach((img, index) => {
        const formattedImage = formatImageSrc(img);
        if (formattedImage) {
          allImages.push(formattedImage);
          imageLabels.push(`Local Image ${index + 1}`);
        }
      });
    }

    console.log("Collected images count:", allImages.length);
    console.log("First image preview:", allImages[0]?.substring(0, 100));

    if (allImages.length > 0) {
      // Create an array of objects with formatted image and label
      const imagesWithLabels = allImages.map((img, index) => ({
        src: img,
        label: imageLabels[index] || `Image ${index + 1}`,
      }));

      setCurrentImages(imagesWithLabels);
      setSelectedRecord(record);
      setCurrentStatus(getStatusLabel(record.globalImeiStatus));
      setZoomLevel(1);
      setImageModalVisible(true);
    } else {
      console.error("No images found in record");
      alert("No images available");
    }
  };

  const selectedImeiDetails = useSelector((state) => state.GlobalImei.single);

  const handleViewUserDetails = async (record) => {
    setSelectedRecord(record);
    setUserModalVisible(true);
    setLoadingUserDetails(true);

    try {
      await dispatch(getGlobalImeiCodeById({ id: record.id }));
      console.log("Fetched user details for ID:", record.id);
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleViewFullDetails = (record) => {
    setSelectedImeiForDetails(record);
    setDetailsModalVisible(true);
  };

  const handleDelete = async (record) => {
    const recordId = record?._id || record?.id;

    const { default: Swal } = await import("sweetalert2");

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This IMEI record will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteGlobalImei(recordId));
        await dispatch(getGlobalImeiCode());
      } catch (error) {
        console.error("Error deleting IMEI record:", error);
      }
    }
  };
  const statusMap = {
    Pending: 0,
    Approved: 1,
    Rejected: 2,
    Completed: 3,
  };

  const handleUpdate = async (statusLabel) => {
    setCurrentStatus(statusLabel);
    const parsedStatus = statusMap[statusLabel];

    const updatedData = {
      userId: selectedRecord.userId,
      customerId: selectedRecord.customerId || "",
      databaseName: selectedRecord.databaseName || "",
      id: selectedRecord.id || "",
      imeiNo: selectedRecord.imeiNo || "",
      globalImeiStatus: parsedStatus || "",
      createdBy: selectedRecord.createdBy || "",
      reason: selectedRecord.reason || "",
      status: selectedRecord.status || "",
      color: selectedRecord.color || "",
      brand: selectedRecord.brand || "",
      model: selectedRecord.model || "",
      ram: selectedRecord.ram || "",
      rom: selectedRecord.rom || "",
      firNumber: selectedRecord.firNumber || "",
      fullDeviceModel: selectedRecord.fullDeviceModel || "",
      imeiNo2: selectedRecord.imeiNo2 || "",
      storage: selectedRecord.storage || "",
      currentAddress: selectedRecord.currentAddress || "",
      policeStation: selectedRecord.policeStation || "",
      theftLocation: selectedRecord.theftLocation || "",
      loggedInEmail: selectedRecord.loggedInEmail || "",
    };
    await dispatch(updateGlobalImeiStatus(updatedData));
    await dispatch(getGlobalImeiCode());
  };

  const handleUpdateFromTable = async (record, statusLabel) => {
    const parsedStatus = statusMap[statusLabel];

    if (!record.imeiNo) {
      console.error("IMEI number is required");
      return;
    }
    if (!record.createdBy) {
      console.error("CreatedBy is required");
      return;
    }

    const updatedData = {
      id: record.id,
      // databaseName: record.databaseName || "",
      // imeiNo: record.imeiNo,
      globalImeiStatus: parsedStatus,
      // createdBy: record.createdBy,
      // reason: record.reason || "",
      // status: record.status !== undefined ? record.status : true,
      // color: record.color || "",
      // brand: record.brand || "",
      // model: record.model || "",
      // ram: record.ram || "",
      // rom: record.rom || "",
      // firNumber: record.firNumber || "",
      // fullDeviceModel: record.fullDeviceModel || "",
      // imeiNo2: record.imeiNo2 || "",
      // storage: record.storage || "",
      // purchaseDate: record.purchaseDate || "",
      // billNo: record.billNo || "",
      // currentAddress: record.currentAddress || "",
      // alternateContactNo: record.alternateContactNo || "",
      // theftDateTime: record.theftDateTime || "",
      // theftLocation: record.theftLocation || "",
      // briefDescription: record.briefDescription || "",
      // policeStation: record.policeStation || "",
      // investigationOfficerName: record.investigationOfficerName || "",
      // investigationOfficerContact: record.investigationOfficerContact || "",
      // loggedInEmail: record.loggedInEmail || "",
      // otherDetails: record.otherDetails || "",
    };

    if (record.userId != null) updatedData.userId = record.userId;
    if (record.customerId != null) updatedData.customerId = record.customerId;

    try {
      await dispatch(updateGlobalImeiStatus(updatedData));
      await dispatch(getGlobalImeiCode());
    } catch (error) {
      console.error("Error updating IMEI status from table:", error);
    }
  };

  const getStatusBadgeColor = (status) => {
    if (status === 0 || status === false) return "warning";
    if (status === 1) return "info";
    if (status === 2) return "danger";
    if (status === 3) return "success";
    return "secondary";
  };

  const getStatusLabel = (status) => {
    if (status === 0 || status === false) return "Pending";
    if (status === 1) return "Approved";
    if (status === 3) return "Completed";
    return "Rejected";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN");
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "-";
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString("en-IN");
    } catch (error) {
      return dateTimeString;
    }
  };

  const filteredData = globalImeiCode
    ? globalImeiCode.filter((item) => {
      const search = searchTerm.toLowerCase();
      const imeiMatch = item.imeiNo?.toLowerCase().includes(search);
      const imei2Match = item.imeiNo2?.toLowerCase().includes(search);
      const fullModelMatch = item.fullDeviceModel
        ?.toLowerCase()
        .includes(search);
      const firMatch = item.firNumber?.toLowerCase().includes(search);
      const policeMatch = item.policeStation?.toLowerCase().includes(search);
      const addressMatch = item.currentAddress
        ?.toLowerCase()
        .includes(search);
      const billMatch = item.billNo?.toLowerCase().includes(search);
      const emailMatch = item.loggedInEmail?.toLowerCase().includes(search);
      const mobileMatch = item.user?.mobile?.includes(searchTerm);
      const nameMatch = item.user?.name?.toLowerCase().includes(search);
      const brandMatch = item.brand?.toLowerCase().includes(search);
      const modelMatch = item.model?.toLowerCase().includes(search);
      const colorMatch = item.color?.toLowerCase().includes(search);
      const storageMatch = item.storage?.toLowerCase().includes(search);
      const ramMatch = item.ram?.toLowerCase().includes(search);

      const status = getStatusLabel(item.globalImeiStatus);

      const statusMatch = filterStatus === "All" || status === filterStatus;

      const searchMatch =
        imeiMatch ||
        imei2Match ||
        fullModelMatch ||
        firMatch ||
        policeMatch ||
        addressMatch ||
        billMatch ||
        emailMatch ||
        mobileMatch ||
        nameMatch ||
        brandMatch ||
        modelMatch ||
        colorMatch ||
        storageMatch ||
        ramMatch;

      const isOtherUser = !!item.databaseName; // has databaseName → Other User
      const isPortalUser = !item.databaseName; // no databaseName  → Portal User

      if (filterStatus === "MissingUserInfo") {
        return isOtherUser && searchMatch;
      }
      if (filterStatus === "FoundUserInfo") {
        return isPortalUser && searchMatch;
      }

      return statusMatch && searchMatch;
    })
    : [];

  return (
    <div className="page-content">
      <Breadcrumb className="mb-3">
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Global IMEI</Breadcrumb.Item>
      </Breadcrumb>

      <Container fluid>
        <Card className="shadow-sm">
          <CardBody>
            <Row className="mb-4">
              <Col lg="8" className="mb-3 mb-lg-0">
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    color={
                      filterStatus === "All" ? "primary" : "outline-primary"
                    }
                    onClick={() => setFilterStatus("All")}
                    className="shadow-sm"
                  >
                    All Records
                  </Button>
                  <Button
                    size="md"
                    color={
                      filterStatus === "Approved"
                        ? "info"
                        : "outline-info"
                    }
                    onClick={() => setFilterStatus("Approved")}
                    className="shadow-sm"
                  >
                    <i className="mdi mdi-check-circle me-1"></i>
                    Approved
                  </Button>
                  <Button
                    size="sm"
                    color={
                      filterStatus === "Completed"
                        ? "success"
                        : "outline-success"
                    }
                    onClick={() => setFilterStatus("Completed")}
                    className="shadow-sm"
                  >
                    <i className="mdi mdi-check-circle me-1"></i>
                    Completed
                  </Button>
                  <Button
                    size="sm"
                    color={
                      filterStatus === "Pending" ? "warning" : "outline-warning"
                    }
                    onClick={() => setFilterStatus("Pending")}
                    className="shadow-sm"
                  >
                    <i className="mdi mdi-clock-outline me-1"></i>
                    Pending
                  </Button>
                  <Button
                    size="sm"
                    color={
                      filterStatus === "Rejected" ? "danger" : "outline-danger"
                    }
                    onClick={() => setFilterStatus("Rejected")}
                    className="shadow-sm"
                  >
                    <i className="mdi mdi-close-circle me-1"></i>
                    Rejected
                  </Button>
                  <Button
                    size="sm"
                    color={
                      filterStatus === "MissingUserInfo"
                        ? "dark"
                        : "outline-dark"
                    }
                    onClick={() => setFilterStatus("MissingUserInfo")}
                    className="shadow-sm"
                  >
                    <i className="mdi mdi-account-question me-1"></i>
                    Other Users
                  </Button>
                  <Button
                    size="sm"
                    color={
                      filterStatus === "FoundUserInfo" ? "info" : "outline-info"
                    }
                    onClick={() => setFilterStatus("FoundUserInfo")}
                    className="shadow-sm"
                  >
                    <i className="mdi mdi-account-check me-1"></i>
                    Portal Users
                  </Button>
                </div>
              </Col>
              <Col lg="4">
                <Input
                  type="text"
                  placeholder="🔍 Search IMEI, FIR, Police Station..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="shadow-sm"
                />
              </Col>
            </Row>

            <Row className="mb-3 align-items-center">
              <Col md="6">
                <h5 className="text-primary mb-0">
                  <i className="mdi mdi-table-large me-2"></i>
                  IMEI Records
                </h5>
              </Col>
              <Col md="6" className="text-end">
                <Badge color="primary" pill className="fs-6 px-3 py-2">
                  {filteredData.length} Record
                  {filteredData.length !== 1 ? "s" : ""}
                </Badge>
              </Col>
            </Row>

            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner
                  color="primary"
                  style={{ width: "3rem", height: "3rem" }}
                />
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  hover
                  bordered
                  striped
                  className="w-full align-middle mb-0 text-nowrap"
                >
                  <thead className="table-primary">
                    <tr>
                      <th>IMEI No</th>
                      <th>IMEI No 2</th>
                      <th>Full Device Model</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Action</th>
                      <th>Brand</th>
                      <th>Model</th>
                      <th>Color</th>
                      <th>Storage</th>
                      <th>RAM</th>
                      <th>FIR Number</th>
                      <th>Police Station</th>
                      <th>Current Address</th>
                      <th>Purchase Date</th>
                      <th>Bill No</th>
                      <th>Theft Date</th>
                      <th>Theft Location</th>
                      <th>Email/Apple ID</th>
                      <th>User</th>
                      <th className="text-center">Images</th>
                      <th className="text-center">Details</th>
                      <th className="text-center">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length > 0 ? (
                      filteredData.map((item) => (
                        <tr key={item._id || item.id}>
                          <td className="font-monospace">
                            {item.imeiNo || "-"}
                          </td>
                          <td className="font-monospace">
                            {item.imeiNo2 || "-"}
                          </td>
                          <td>{item.fullDeviceModel || "-"}</td>
                          <td className="text-center">
                            <Badge
                              color={getStatusBadgeColor(item.globalImeiStatus)}
                              pill
                              className="px-3"
                            >
                              {getStatusLabel(item.globalImeiStatus)}
                            </Badge>
                          </td>
                          <td style={{ minWidth: "120px" }}>
                            <Input
                              type="select"
                              value={getStatusLabel(item.globalImeiStatus)}
                              onChange={(e) =>
                                handleUpdateFromTable(item, e.target.value)
                              }
                              bsSize="sm"
                              className="fw-semibold w-100"
                            >
                              <option value="Pending">Pending</option>
                              <option value="Approved">Approved</option>
                              <option value="Rejected">Rejected</option>
                              <option value="Completed">Completed</option>
                            </Input>
                          </td>
                          <td>{item.brand || "-"}</td>
                          <td>{item.model || "-"}</td>
                          <td>{item.color || "-"}</td>
                          <td>{item.storage || item.rom || "-"}</td>
                          <td>{item.ram || "-"}</td>
                          <td>{item.firNumber || "-"}</td>
                          <td>{item.policeStation || "-"}</td>
                          <td>
                            {item.currentAddress ? (
                              <span className="small">
                                {item.currentAddress.length > 30
                                  ? `${item.currentAddress.substring(0, 30)}...`
                                  : item.currentAddress}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="text-nowrap">
                            {formatDate(item.purchaseDate)}
                          </td>
                          <td>{item.billNo || "-"}</td>
                          <td className="text-nowrap">
                            {formatDateTime(item.theftDateTime)}
                          </td>
                          <td>
                            {item.theftLocation ? (
                              <span className="small">
                                {item.theftLocation.length > 30
                                  ? `${item.theftLocation.substring(0, 30)}...`
                                  : item.theftLocation}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {item.loggedInEmail ? (
                              <a
                                href={`mailto:${item.loggedInEmail}`}
                                className="text-primary small"
                              >
                                {item.loggedInEmail.length > 20
                                  ? `${item.loggedInEmail.substring(0, 20)}...`
                                  : item.loggedInEmail}
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {item.user && item.user.name ? (
                              <div>
                                <div className="fw-semibold">
                                  {item.user.name}
                                </div>
                                <small className="text-muted">
                                  {item.user.mobile}
                                </small>
                              </div>
                            ) : (
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => handleViewUserDetails(item)}
                                className="shadow-sm"
                              >
                                <EyeOutlined /> View
                              </Button>
                            )}
                          </td>
                          <td className="text-center">
                            {(item.imagesLive && item.imagesLive.length > 0) ||
                              (item.imagesLocal && item.imagesLocal.length > 0) ||
                              item.govIdProof ||
                              item.originalBoxPhoto ||
                              item.addressProofPhoto ||
                              item.purchaseBillPhoto ||
                              item.firPhoto ? (
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleViewImages(item)}
                                className="shadow-sm"
                              >
                                <EyeOutlined /> View Images
                              </Button>
                            ) : (
                              <span className="text-muted small">
                                No images
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            <Button
                              color="secondary"
                              size="sm"
                              onClick={() => handleViewFullDetails(item)}
                              className="shadow-sm"
                            >
                              <EyeOutlined /> View
                            </Button>
                          </td>
                          <td className="text-center">
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(item)}
                              className="shadow-sm"
                            >
                              <DeleteOutlined />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="22" className="text-center py-4">
                          {" "}
                          <div className="text-muted">
                            <i
                              className="mdi mdi-alert-circle-outline me-2"
                              style={{ fontSize: "1.5rem" }}
                            ></i>
                            <div>No IMEI records found</div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* Full Details Modal */}
      <Modal
        isOpen={detailsModalVisible}
        toggle={() => setDetailsModalVisible(!detailsModalVisible)}
        size="xl"
      >
        <ModalHeader toggle={() => setDetailsModalVisible(false)}>
          <i className="mdi mdi-information-outline me-2"></i>
          Complete IMEI Details
        </ModalHeader>

        <ModalBody>
          {selectedImeiForDetails ? (
            <div>
              <Row>
                <Col md="6">
                  <Card className="border mb-3">
                    <CardHeader className="bg-light">
                      <h6 className="mb-0">
                        <i className="mdi mdi-cellphone me-2"></i>
                        Device Information
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          IMEI No:
                        </Col>
                        <Col xs="7" className="font-monospace">
                          {selectedImeiForDetails.imeiNo || "-"}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          IMEI No 2:
                        </Col>
                        <Col xs="7" className="font-monospace">
                          {selectedImeiForDetails.imeiNo2 || "-"}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Full Model:
                        </Col>
                        <Col xs="7">
                          {selectedImeiForDetails.fullDeviceModel || "-"}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Brand:
                        </Col>
                        <Col xs="7">{selectedImeiForDetails.brand || "-"}</Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Model:
                        </Col>
                        <Col xs="7">{selectedImeiForDetails.model || "-"}</Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Color:
                        </Col>
                        <Col xs="7">{selectedImeiForDetails.color || "-"}</Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Storage:
                        </Col>
                        <Col xs="7">
                          {selectedImeiForDetails.storage ||
                            selectedImeiForDetails.rom ||
                            "-"}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          RAM:
                        </Col>
                        <Col xs="7">{selectedImeiForDetails.ram || "-"}</Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <Card className="border">
                    <CardHeader className="bg-light">
                      <h6 className="mb-0">
                        <i className="mdi mdi-file-document me-2"></i>
                        FIR Details
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          FIR Number:
                        </Col>
                        <Col xs="7">
                          {selectedImeiForDetails.firNumber || "-"}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>

                <Col md="6">
                  <Card className="border mb-3">
                    <CardHeader className="bg-light">
                      <h6 className="mb-0">
                        <i className="mdi mdi-cart me-2"></i>
                        Purchase Information
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Purchase Date:
                        </Col>
                        <Col xs="7">
                          {formatDate(selectedImeiForDetails.purchaseDate)}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Bill No:
                        </Col>
                        <Col xs="7">{selectedImeiForDetails.billNo || "-"}</Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <Card className="border mb-3">
                    <CardHeader className="bg-light">
                      <h6 className="mb-0">
                        <i className="mdi mdi-alert me-2"></i>
                        Theft Details
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Theft Date:
                        </Col>
                        <Col xs="7">
                          {formatDateTime(selectedImeiForDetails.theftDateTime)}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Theft Location:
                        </Col>
                        <Col xs="7">
                          {selectedImeiForDetails.theftLocation || "-"}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Brief Description:
                        </Col>
                        <Col xs="7">
                          {selectedImeiForDetails.briefDescription || "-"}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <Card className="border">
                    <CardHeader className="bg-light">
                      <h6 className="mb-0">
                        <i className="mdi mdi-shield-account me-2"></i>
                        Police Details
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Police Station:
                        </Col>
                        <Col xs="7">
                          {selectedImeiForDetails.policeStation || "-"}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Investigation Officer:
                        </Col>
                        <Col xs="7">
                          {selectedImeiForDetails.investigationOfficerName ||
                            "-"}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col xs="5" className="fw-bold text-muted">
                          Officer Contact:
                        </Col>
                        <Col xs="7">
                          {selectedImeiForDetails.investigationOfficerContact ||
                            "-"}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Card className="border mt-3">
                <CardHeader className="bg-light">
                  <h6 className="mb-0">
                    <i className="mdi mdi-account-details me-2"></i>
                    Owner Details
                  </h6>
                </CardHeader>
                <CardBody>
                  <Row className="mb-2">
                    <Col xs="2" className="fw-bold text-muted">
                      Current Address:
                    </Col>
                    <Col xs="10">
                      {selectedImeiForDetails.currentAddress || "-"}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="2" className="fw-bold text-muted">
                      Alternate Contact:
                    </Col>
                    <Col xs="10">
                      {selectedImeiForDetails.alternateContactNo || "-"}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="2" className="fw-bold text-muted">
                      Email/Apple ID:
                    </Col>
                    <Col xs="10">
                      {selectedImeiForDetails.loggedInEmail || "-"}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs="2" className="fw-bold text-muted">
                      Other Details:
                    </Col>
                    <Col xs="10">
                      {selectedImeiForDetails.otherDetails || "-"}
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {selectedImeiForDetails.user && (
                <Card className="border mt-3">
                  <CardHeader className="bg-light">
                    <h6 className="mb-0">
                      <i className="mdi mdi-account-circle me-2"></i>
                      User Information
                    </h6>
                  </CardHeader>
                  <CardBody>
                    <Row className="mb-2">
                      <Col xs="2" className="fw-bold text-muted">
                        Name:
                      </Col>
                      <Col xs="10">
                        {selectedImeiForDetails.user.name || "-"}
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs="2" className="fw-bold text-muted">
                        Mobile:
                      </Col>
                      <Col xs="10">
                        {selectedImeiForDetails.user.mobile || "-"}
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs="2" className="fw-bold text-muted">
                        Status:
                      </Col>
                      <Col xs="10">
                        <Badge
                          color={
                            selectedImeiForDetails.user.status
                              ? "success"
                              : "danger"
                          }
                          pill
                        >
                          {selectedImeiForDetails.user.status
                            ? "Active"
                            : "Inactive"}
                        </Badge>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-5">
              <i
                className="mdi mdi-alert-circle-outline text-muted"
                style={{ fontSize: "3rem" }}
              ></i>
              <p className="text-muted mt-3">No details available</p>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setDetailsModalVisible(false)}
          >
            <i className="mdi mdi-close me-1"></i>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Image Modal */}
      <Modal
        isOpen={imageModalVisible}
        toggle={() => setImageModalVisible(!imageModalVisible)}
        size="lg"
      >
        <ModalHeader toggle={() => setImageModalVisible(false)}>
          <i className="mdi mdi-image-multiple me-2"></i>
          Device Images - {selectedRecord?.imeiNo || ""}
        </ModalHeader>
        <ModalBody>
          {currentImages && currentImages.length > 0 ? (
            <div>
              <Row className="mb-3">
                <Col>
                  <Badge color="info" className="mb-2">
                    {currentImages.length} Image
                    {currentImages.length !== 1 ? "s" : ""} Found
                  </Badge>
                </Col>
              </Row>
              <Row>
                {currentImages.map((image, index) => (
                  <Col md="6" key={index} className="mb-4">
                    <Card className="border shadow-sm">
                      <CardHeader className="bg-light py-2">
                        <small className="fw-bold">{image.label}</small>
                      </CardHeader>
                      <div
                        className="position-relative"
                        style={{ height: "300px", cursor: "zoom-in" }}
                        onClick={() => setPreviewImage(image)} // 👈 ADD THIS
                      >
                        <img
                          src={image.src}
                          alt={image.label}
                          className="img-fluid h-100 w-100"
                          style={{
                            objectFit: "contain",
                            backgroundColor: "#f8f9fa",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML = `
                        <div class="d-flex flex-column justify-content-center align-items-center h-100">
                          <i class="mdi mdi-image-broken text-danger" style="font-size: 48px"></i>
                          <p class="text-muted mt-2">Failed to load image</p>
                          <small class="text-danger">${image.label}</small>
                        </div>
                      `;
                          }}
                        />
                        {/* Zoom hint overlay */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: "8px",
                            right: "8px",
                            background: "rgba(0,0,0,0.45)",
                            color: "#fff",
                            borderRadius: "6px",
                            padding: "3px 8px",
                            fontSize: "11px",
                            pointerEvents: "none",
                          }}
                        >
                          Click to preview
                        </div>
                      </div>
                      <CardBody className="text-center py-2">
                        <Badge color="secondary" pill className="me-2">
                          {index + 1}
                        </Badge>
                        <small className="text-muted">
                          {image.src?.startsWith("data:")
                            ? "Base64 Image"
                            : "URL Image"}
                        </small>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ) : (
            <div className="text-center py-5">
              <i
                className="mdi mdi-image-off text-muted"
                style={{ fontSize: "3rem" }}
              ></i>
              <p className="text-muted mt-3">No images available</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setImageModalVisible(false)}>
            <i className="mdi mdi-close me-1"></i>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* ───── Lightbox Preview ───── */}
      {previewImage && (
        <div
          onClick={() => {
            setPreviewImage(null);
            setZoomLevel(1);
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            overflow: "hidden",
          }}
        >
          {/* Close button */}
          <button
            onClick={() => {
              setPreviewImage(null);
              setZoomLevel(1);
            }}
            style={{
              position: "fixed",
              top: "16px",
              right: "20px",
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              color: "#fff",
              fontSize: "20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10001,
            }}
          >
            ×
          </button>

          {/* Label */}
          <div
            style={{
              color: "#fff",
              marginBottom: "12px",
              fontSize: "14px",
              fontWeight: 500,
              background: "rgba(255,255,255,0.1)",
              padding: "4px 16px",
              borderRadius: "20px",
              zIndex: 10001,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {previewImage.label}
          </div>

          {/* Image */}
          <img
            src={previewImage.src}
            alt={previewImage.label}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "75vh",
              objectFit: "contain",
              borderRadius: "8px",
              transform: `scale(${zoomLevel})`,
              transition: "transform 0.2s ease",
              cursor: zoomLevel > 1 ? "zoom-out" : "default",
            }}
          />

          {/* Zoom Controls */}
          {/* Zoom Controls */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              position: "fixed",
              bottom: "24px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(30,30,30,0.85)",
              padding: "8px 16px",
              borderRadius: "30px",
              zIndex: 10001,
              backdropFilter: "blur(6px)",
              whiteSpace: "nowrap",
            }}
          >
            {/* Zoom Out */}
            <button
              onClick={() =>
                setZoomLevel((prev) =>
                  Math.max(0.5, parseFloat((prev - 0.25).toFixed(2))),
                )
              }
              disabled={zoomLevel <= 0.5}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "50%",
                width: "34px",
                height: "34px",
                color: "#fff",
                fontSize: "20px",
                cursor: zoomLevel <= 0.5 ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: zoomLevel <= 0.5 ? 0.4 : 1,
              }}
            >
              −
            </button>

            {/* Zoom Level Display */}
            <span
              style={{
                color: "#fff",
                fontSize: "13px",
                minWidth: "44px",
                textAlign: "center",
              }}
            >
              {Math.round(zoomLevel * 100)}%
            </span>

            {/* Zoom In */}
            <button
              onClick={() =>
                setZoomLevel((prev) =>
                  Math.min(3, parseFloat((prev + 0.25).toFixed(2))),
                )
              }
              disabled={zoomLevel >= 3}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "50%",
                width: "34px",
                height: "34px",
                color: "#fff",
                fontSize: "20px",
                cursor: zoomLevel >= 3 ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: zoomLevel >= 3 ? 0.4 : 1,
              }}
            >
              +
            </button>

            {/* Reset Zoom */}
            <button
              onClick={() => setZoomLevel(1)}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "6px",
                padding: "4px 12px",
                color: "#fff",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              Reset
            </button>

            {/* Download */}
            <a
              href={previewImage.src}
              download={`${previewImage.label}.jpg`}
              onClick={(e) => e.stopPropagation()}
              style={{
                color: "#fff",
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "6px",
                padding: "4px 12px",
                fontSize: "12px",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <i className="mdi mdi-download"></i> Download
            </a>
          </div>

          {/* Close hint */}
          <div
            style={{
              position: "fixed",
              bottom: "72px",
              color: "rgba(255,255,255,0.4)",
              fontSize: "12px",
              zIndex: 10001,
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            Click outside to close
          </div>
        </div>
      )}

      {/* User Details Modal */}
      <Modal
        isOpen={userModalVisible}
        toggle={() => setUserModalVisible(!userModalVisible)}
        size="lg"
      >
        <ModalHeader toggle={() => setUserModalVisible(false)}>
          <i className="mdi mdi-account-details me-2"></i>
          User Details
        </ModalHeader>
        <ModalBody>
          {loadingUserDetails ? (
            <div className="text-center py-5">
              <Spinner
                color="primary"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-muted mt-3">Loading user details...</p>
            </div>
          ) : selectedImeiDetails ? (
            <Card className="border">
              <CardBody>
                <Row className="mb-3">
                  <Col xs="4" className="fw-bold text-muted">
                    User ID:
                  </Col>
                  <Col xs="8">{selectedImeiDetails.userId || "-"}</Col>
                </Row>
                <Row className="mb-3">
                  <Col xs="4" className="fw-bold text-muted">
                    Customer ID:
                  </Col>
                  <Col xs="8">{selectedImeiDetails.customerId || "-"}</Col>
                </Row>
                <Row className="mb-3">
                  <Col xs="4" className="fw-bold text-muted">
                    Database Name:
                  </Col>
                  <Col xs="8">{selectedImeiDetails.databaseName || "-"}</Col>
                </Row>
                <Row className="mb-3">
                  <Col xs="4" className="fw-bold text-muted">
                    Created By:
                  </Col>
                  <Col xs="8">{selectedImeiDetails.createdBy || "-"}</Col>
                </Row>
                {selectedImeiDetails.user && (
                  <>
                    <hr />
                    <h6 className="text-primary mb-3">
                      <i className="mdi mdi-account-circle me-2"></i>
                      User Information
                    </h6>
                    <Row className="mb-3">
                      <Col xs="4" className="fw-bold text-muted">
                        Name:
                      </Col>
                      <Col xs="8">{selectedImeiDetails.user.name || "-"}</Col>
                    </Row>
                    <Row className="mb-3">
                      <Col xs="4" className="fw-bold text-muted">
                        Mobile:
                      </Col>
                      <Col xs="8">{selectedImeiDetails.user.mobile || "-"}</Col>
                    </Row>
                    <Row className="mb-3">
                      <Col xs="4" className="fw-bold text-muted">
                        Email:
                      </Col>
                      <Col xs="8">{selectedImeiDetails.user.email || "-"}</Col>
                    </Row>
                    <Row className="mb-3">
                      <Col xs="4" className="fw-bold text-muted">
                        Status:
                      </Col>
                      <Col xs="8">
                        <Badge
                          color={
                            selectedImeiDetails.user.status
                              ? "success"
                              : "danger"
                          }
                          pill
                        >
                          {selectedImeiDetails.user.status
                            ? "Active"
                            : "Inactive"}
                        </Badge>
                      </Col>
                    </Row>
                  </>
                )}
              </CardBody>
            </Card>
          ) : (
            <div className="text-center py-4">
              <i
                className="mdi mdi-account-alert text-muted"
                style={{ fontSize: "3rem" }}
              ></i>
              <p className="text-muted mt-3">No user details found</p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setUserModalVisible(false)}>
            <i className="mdi mdi-close me-1"></i>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default GlobalImei;
